
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, CalendarIcon, Clock, Building, Eye } from "lucide-react";
import { format } from "date-fns";

const CompletedExamsTab = ({ completedExams, selectedCompany, onViewResult, companies, loadingResult }) => {
  const selectedCompanyObj = companies?.find((c) => c.id === selectedCompany);
  const selectedCompanyName = selectedCompanyObj?.name || "Unknown Company";
  

  const getExamStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Pending" },
      ongoing: { color: "bg-blue-100 text-blue-800", text: "In Progress" },
      completed: { color: "bg-green-100 text-green-800", text: "Completed" },
      disqualified: { color: "bg-red-100 text-red-800", text: "Disqualified" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const getReviewStatusBadge = (reviewStatus) => {
    const statusConfig = {
      pending: { color: "bg-yellow-100 text-yellow-800", text: "Under Review" },
      reviewed: { color: "bg-green-100 text-green-800", text: "Reviewed" },
      flagged: { color: "bg-red-100 text-red-800", text: "Flagged" },
    };

    const config = statusConfig[reviewStatus] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.text}
      </span>
    );
  };

  if (!selectedCompany || selectedCompany === "") {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Select a Company</h3>
            <p className="text-gray-600 text-center">
              Please select a company from the dropdown to view completed exams.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!completedExams || completedExams.length === 0) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle2 className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Data Available</h3>
            <p className="text-gray-600 text-center">
              No completed exams for the selected company.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-4">
        {completedExams.map((assignment) => (
          <Card key={assignment._id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">{assignment.examId?.name || "Unknown Exam"}</CardTitle>
                  <p className="text-sm text-gray-600 mt-1">{assignment.examId?.description || "No description available"}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Building className="h-4 w-4 text-gray-500" />
                    <span className="text-sm text-gray-600">
                      {selectedCompanyName}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  {getExamStatusBadge(assignment.status)}
                  {assignment.resultId?.reviewStatus &&
                    getReviewStatusBadge(assignment.resultId.reviewStatus)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="text-sm">
                    Completed: {assignment.dateCompleted
                      ? format(new Date(assignment.dateCompleted), "PPP")
                      : "N/A"}
                  </span>
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-gray-500" />
                  <span className="text-sm">Duration: {assignment.totalTimeAllotted || 0} minutes</span>
                </div>
              </div>

              {assignment.resultId && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-wrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        assignment.resultId.isPassed
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {assignment.resultId.isPassed ? "PASSED" : "FAILED"}
                    </span>
                    <span className="text-sm text-gray-600">
                      Score: {assignment.resultId.percentageScore || 0}%
                    </span>
                    <span className="text-sm text-gray-600">
                      ({assignment.resultId.correctAnswers || 0}/{assignment.resultId.totalQuestions || 0})
                    </span>
                    {assignment.resultId.warningCount > 0 && (
                      <span className="text-sm text-orange-600">
                        ⚠️ {assignment.resultId.warningCount} warnings
                      </span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewResult(assignment)}
                    disabled={loadingResult}
                    className="flex items-center gap-2"
                  >
                    <Eye className="h-4 w-4" />
                    {loadingResult ? "Loading..." : "View Result"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CompletedExamsTab;
